import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Rating,
  Chip,
  TextField,
  Button,
  Paper,
  Fade,
  Stack,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import {
  ThumbUp,
  Send,
  CheckCircle,
  ErrorOutline,
  ArrowForward,
  ArrowBack,
} from '@mui/icons-material';
import { saveEvaluation } from '../services/evaluationService';
import { getActiveEvaluationOptions, getEvaluationOptionsForCourse, getEvaluationOptionsByConfigId } from '../services/evaluationOptionsService';

const TrainingEvaluation = () => {
  const [rating, setRating] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [evaluationOptions, setEvaluationOptions] = useState({
    positive: [
      'Conteúdo relevante',
      'Dinâmica das atividades',
      'Aplicabilidade no trabalho',
      'Materiais de apoio',
      'Duração',
      'Reprojetos audiovisuais',
      'Organização do treinamento',
    ],
    improvement: [
      'Mais exemplos práticos',
      'Melhor ritmo das explicações',
      'Material complementar mais detalhado',
      'Ajustar carga horária',
    ]
  });

  // Estados para capturar dados da URL e do Firestore
  const [courseName, setCourseName] = useState(null);
  const [userId, setUserId] = useState(null);
  const [configId, setConfigId] = useState(null);
  const [isScorm, setIsScorm] = useState(false);
  const [evaluationPageTitle, setEvaluationPageTitle] = useState('O que você achou do treinamento?');

  // Estados para navegação horizontal (SCORM)
  const [activeStep, setActiveStep] = useState(0);

  // Captura apenas o configId da URL ao carregar o componente
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const configParam = params.get('configId');

    if (configParam) {
      setConfigId(configParam);
      setAccessDenied(false);
      // console.log('Configuração específica detectada:', configParam);
    } else {
      // Bloqueia acesso se não houver configId
      setAccessDenied(true);
      console.warn('Acesso negado: configId não fornecido na URL');
    }
  }, []);

  // Carrega as opções de avaliação e dados do Firestore dinamicamente
  useEffect(() => {
    const loadEvaluationOptions = async () => {
      try {
        // Se houver um configId, busca as opções e dados da configuração
        if (configId) {
          // console.log('Buscando configuração completa para configId:', configId);
          const config = await getEvaluationOptionsByConfigId(configId);
          // console.log('Configuração recebida:', config);

          // Atualiza as opções de avaliação
          setEvaluationOptions({
            positive: config.positive || [],
            improvement: config.improvement || []
          });

          // Atualiza courseName e userId do Firestore
          if (config.courseName) {
            setCourseName(config.courseName);
          }
          if (config.userId) {
            setUserId(config.userId);
          }
          // Atualiza isScorm
          setIsScorm(config.isScorm || false);
          // Atualiza o título da página de avaliação
          setEvaluationPageTitle(config.evaluationTitle || 'O que você achou do treinamento?');
        } else {
          // Caso contrário, busca todas as opções ativas
          // console.log('Buscando opções ativas padrão');
          const options = await getActiveEvaluationOptions();
          // console.log('Opções ativas padrão recebidas:', options);
          setEvaluationOptions(options);
          setEvaluationPageTitle('O que você achou do treinamento?');
        }
      } catch (error) {
        console.error('Erro ao carregar opções de avaliação:', error);
        // As opções padrão já estão definidas no estado inicial
      }
    };

    // Carrega as opções quando o configId estiver disponível
    if (configId !== null) {
      loadEvaluationOptions();
    }
  }, [configId]);

  const ratingLabels = {
    1: 'Muito insatisfeito',
    2: 'Insatisfeito',
    3: 'Regular',
    4: 'Satisfeito',
    5: 'Muito satisfeito',
  };

  const handleRatingChange = (event, newValue) => {
    setRating(newValue);
    setSelectedOptions([]);
    setShowValidation(false);
  };

  const handleOptionToggle = (option) => {
    setSelectedOptions((prev) =>
      prev.includes(option)
        ? prev.filter((item) => item !== option)
        : [...prev, option]
    );
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      setShowValidation(true);
      return;
    }

    setLoading(true);
    setError(null);

    const evaluationData = {
      rating,
      selectedOptions,
      comment,
      // Campos preenchidos automaticamente via URL ou manualmente
      userId: userId || 'anonimo',
      courseName: courseName || null,
      configId: configId || null, // Adiciona o ID da configuração para referência
      isScorm: isScorm || false, // Indica se é um pacote SCORM
    };

    try {
      const result = await saveEvaluation(evaluationData);

      if (result.success) {
        // console.log('✅ Avaliação salva com ID:', result.id);
        setSubmitted(true);
      } else {
        setError(result.message || 'Erro ao salvar avaliação. Tente novamente.');
      }
    } catch (err) {
      console.error('Erro ao enviar avaliação:', err);
      setError('Erro ao enviar avaliação. Verifique sua conexão e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const renderOptions = () => {
    const options = rating >= 4 ? evaluationOptions.positive : evaluationOptions.improvement;
    const title = rating >= 4 ? 'Do que você mais gostou?' : 'O que poderia melhorar?';

    return (
      <Fade in={true} timeout={500}>
        <Box sx={{ mt: 2.5 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 1.5,
              color: 'text.primary',
              fontSize: '1rem',
            }}
          >
            {title}
          </Typography>
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={1}
            sx={{ mb: 2 }}
          >
            {options.map((option) => (
              <Chip
                key={option}
                label={option}
                onClick={() => handleOptionToggle(option)}
                color={selectedOptions.includes(option) ? 'primary' : 'default'}
                variant={selectedOptions.includes(option) ? 'filled' : 'outlined'}
                sx={{
                  fontSize: '0.8125rem',
                  py: 1.5,
                  px: 0.5,
                  height: 'auto',
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 2,
                  },
                }}
              />
            ))}
          </Stack>
        </Box>
      </Fade>
    );
  };

  // Navegação para SCORM
  const handleNext = () => {
    if (activeStep === 0 && rating === 0) {
      setShowValidation(true);
      return;
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const steps = ['Avaliação', 'Opções', 'Comentário'];

  // Tela de acesso negado
  if (accessDenied) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 3,
          }}
        >
          <Fade in={true} timeout={800}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, sm: 4 },
                textAlign: 'center',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'error.main',
                backgroundColor: 'error.lighter',
              }}
            >
              <ErrorOutline
                sx={{
                  fontSize: 56,
                  color: 'error.main',
                  mb: 1.5,
                }}
              />
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: 'error.dark' }}>
                Acesso Negado
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Esta página requer um link de acesso válido.
                <br />
                Por favor, utilize o link fornecido em seu e-mail ou contato.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Se você acredita que isto é um erro, entre em contato com o administrador.
              </Typography>
            </Paper>
          </Fade>
        </Box>
      </Container>
    );
  }

  if (submitted) {
    return (
      <Container maxWidth={isScorm ? "md" : "sm"}>
        <Box
          sx={{
            minHeight: isScorm ? 'auto' : '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 3,
          }}
        >
          <Fade in={true} timeout={800}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, sm: 4 },
                textAlign: 'center',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <CheckCircle
                sx={{
                  fontSize: 56,
                  color: 'success.main',
                  mb: 1.5,
                }}
              />
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                Obrigado!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sua avaliação foi enviada com sucesso.
                <br />
                Seu feedback é muito importante para nós!
              </Typography>
            </Paper>
          </Fade>
        </Box>
      </Container>
    );
  }

  // Layout horizontal para SCORM
  if (isScorm) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 3 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            }}
          >
            {/* Stepper Horizontal */}
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Conteúdo dos passos */}
            <Box sx={{ minHeight: 300 }}>
              {/* Passo 1: Avaliação */}
              {activeStep === 0 && (
                <Fade in={true} timeout={500}>
                  <Box>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <ThumbUp sx={{ fontSize: 36, color: 'primary.main', mb: 1.5 }} />
                      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {evaluationPageTitle}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Selecione sua avaliação
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <Rating
                        name="training-rating"
                        value={rating}
                        onChange={handleRatingChange}
                        size="large"
                        sx={{
                          fontSize: '2.5rem',
                          '& .MuiRating-iconFilled': { color: '#FFB400' },
                          '& .MuiRating-iconHover': { color: '#FFD700' },
                        }}
                      />
                      {rating > 0 && (
                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 500, color: 'text.secondary' }}>
                          {ratingLabels[rating]}
                        </Typography>
                      )}
                    </Box>

                    {showValidation && rating === 0 && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        Por favor, selecione uma avaliação antes de continuar.
                      </Alert>
                    )}
                  </Box>
                </Fade>
              )}

              {/* Passo 2: Opções */}
              {activeStep === 1 && rating > 0 && (
                <Fade in={true} timeout={500}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
                      {rating >= 4 ? 'Do que você mais gostou?' : 'O que poderia melhorar?'}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1} justifyContent="center">
                      {(rating >= 4 ? evaluationOptions.positive : evaluationOptions.improvement).map((option) => (
                        <Chip
                          key={option}
                          label={option}
                          onClick={() => handleOptionToggle(option)}
                          color={selectedOptions.includes(option) ? 'primary' : 'default'}
                          variant={selectedOptions.includes(option) ? 'filled' : 'outlined'}
                          sx={{
                            fontSize: '0.875rem',
                            py: 1.5,
                            px: 0.5,
                            height: 'auto',
                            borderRadius: 2,
                            transition: 'all 0.3s ease',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: 2 },
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                </Fade>
              )}

              {/* Passo 3: Comentário */}
              {activeStep === 2 && (
                <Fade in={true} timeout={500}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
                      Deixe seu comentário
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      placeholder="Conte-nos como foi sua experiência..."
                      value={comment}
                      onChange={(e) => {
                        if (e.target.value.length <= 300) {
                          setComment(e.target.value);
                        }
                      }}
                      helperText={`${comment.length}/300 caracteres`}
                      variant="outlined"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />

                    {error && (
                      <Alert severity="error" sx={{ mt: 2 }} icon={<ErrorOutline />}>
                        {error}
                      </Alert>
                    )}
                  </Box>
                </Fade>
              )}
            </Box>

            {/* Botões de navegação */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                startIcon={<ArrowBack />}
                sx={{ borderRadius: 2 }}
              >
                Voltar
              </Button>

              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                  endIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Send />}
                  sx={{ borderRadius: 2, fontWeight: 600 }}
                >
                  {loading ? 'Enviando...' : 'Enviar Avaliação'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<ArrowForward />}
                  sx={{ borderRadius: 2, fontWeight: 600 }}
                >
                  Próximo
                </Button>
              )}
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          py: { xs: 2, sm: 3 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 3 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          {/* Cabeçalho */}
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <ThumbUp
              sx={{
                fontSize: 36,
                color: 'primary.main',
                mb: 1.5,
              }}
            />
            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: 700,
                mb: 0.5,
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
              }}
            >
              {evaluationPageTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
              Sua opinião nos ajuda a melhorar sempre
            </Typography>
          </Box>

          {/* Indicador de dados preenchidos automaticamente */}
          {(courseName || userId || configId) && (
            <Fade in={true}>
              <Alert severity="info" sx={{ mb: 2, fontSize: '0.875rem' }}>
                {courseName && (
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    <strong>Projeto:</strong> {courseName}
                  </Typography>
                )}
                {userId && (
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    <strong>Participante:</strong> {userId}
                  </Typography>
                )}
                {/* {configId && (
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    <strong>Configuração:</strong> {configId.substring(0, 8)}...
                  </Typography>
                )} */}
              </Alert>
            </Fade>
          )}

          {/* Avaliação por estrelas */}
          <Box
            sx={{
              mb: 2,
              textAlign: 'center',
              py: 1,
            }}
          >
            <Rating
              name="training-rating"
              value={rating}
              onChange={handleRatingChange}
              size="large"
              sx={{
                fontSize: { xs: '2.25rem', sm: '2.5rem' },
                '& .MuiRating-iconFilled': {
                  color: '#FFB400',
                },
                '& .MuiRating-iconHover': {
                  color: '#FFD700',
                },
              }}
            />
            {rating > 0 && (
              <Fade in={true}>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1,
                    fontWeight: 500,
                    color: 'text.secondary',
                  }}
                >
                  {ratingLabels[rating]}
                </Typography>
              </Fade>
            )}
          </Box>

          {showValidation && rating === 0 && (
            <Fade in={true}>
              <Alert severity="warning" sx={{ mb: 2, fontSize: '0.875rem' }}>
                Por favor, selecione uma avaliação antes de enviar.
              </Alert>
            </Fade>
          )}

          {/* Opções condicionais */}
          {rating > 0 && rating !== 0 && renderOptions()}

          {/* Campo de comentário */}
          {rating > 0 && (
            <Fade in={true} timeout={600}>
              <Box sx={{ mt: 2.5 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    mb: 1.5,
                    color: 'text.primary',
                    fontSize: '1rem',
                  }}
                >
                  Deixe seu comentário
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Conte-nos como foi sua experiência..."
                  value={comment}
                  onChange={(e) => {
                    if (e.target.value.length <= 300) {
                      setComment(e.target.value);
                    }
                  }}
                  helperText={`${comment.length}/300 caracteres`}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontSize: '0.875rem',
                    },
                  }}
                />
              </Box>
            </Fade>
          )}

          {/* Mensagem de erro */}
          {error && (
            <Fade in={true}>
              <Alert
                severity="error"
                sx={{ mt: 2, fontSize: '0.875rem' }}
                icon={<ErrorOutline />}
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            </Fade>
          )}

          {/* Botão de envio */}
          <Box sx={{ mt: 2.5 }}>
            <Button
              fullWidth
              variant="contained"
              size="medium"
              onClick={handleSubmit}
              disabled={loading}
              endIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Send />}
              sx={{
                py: 1.25,
                borderRadius: 2,
                fontSize: '0.9375rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                },
              }}
            >
              {loading ? 'Enviando...' : 'Enviar Avaliação'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default TrainingEvaluation;